import React, { useContext, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Button from 'components/Button';
import { FlyoutContext, FlyoutProvider } from 'components/Flyout/FlyoutContext';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import PillGroup from 'components/PillGroup';
import Spinner from 'components/Spinner';
import SvgDownload from 'components/Svgs/Download/Download';
import { Title } from 'features/App';
import { bem } from 'helpers/class-name';
import { selectIsMobile } from 'selectors/ui';

import { LIFECYCLE_STATUS } from '../shared/SkuEnumHelpers';
import SkuOrderFlyout from '../shared/SkuOrderFlyout';
import useSkuVenues from '../shared/useSkuVenues';

import {
  DATE_RANGE_PRESET,
  findMatchingPreset,
  presetRange,
  toOrdersReportFilter,
  toOrdersReportScope,
} from './SkuOrderReports.filters';
import {
  ORDERS_REPORT_CSV_MAX_ROWS,
  useOrdersReportCsvExport,
  useOrdersReportQuery,
  useOrdersReportSummaryQuery,
} from './SkuOrderReports.hooks';
import messages from './SkuOrderReports.messages';
import SkuOrderReportsFilters from './SkuOrderReportsFilters';
import SkuOrderReportsTable from './SkuOrderReportsTable';
import { FILTER_TYPE, makeReportsTracker, REPORTS_PAGE, SkuReportsTrackingContext } from './SkuReports.tracking';
import SummaryTile, { SummaryTileTrend, SummaryTileTrendDelta, SummaryTileValueLine } from './SkuReportsSummaryTile';
import './SkuOrderReports.scss';

const { block, element } = bem('sku-order-reports');

const EMPTY_VALUE = '—';
const ALL_STATUSES = 'ALL';
const ORDER_FLYOUT_ID = 'sku-order-report-flyout';

const findEarnings = (period, currencyCode) =>
  period.earnings.find(entry => entry.currencyCode === currencyCode)?.amount ?? 0;

const STATUS_OPTIONS = [
  { key: ALL_STATUSES, label: messages.allStatuses },
  { key: LIFECYCLE_STATUS.FULFILLED, label: messages.completedStatus },
  { key: LIFECYCLE_STATUS.CANCELLED, label: messages.cancelledStatus },
];

const SkuOrderReportsPageContent = () => {
  const intl = useIntl();
  const isMobile = useSelector(selectIsMobile);
  const skuVenues = useSkuVenues();
  const showLocationColumn = skuVenues.length > 1;
  const { openFlyout } = useContext(FlyoutContext);

  const [selectedVenueIds, setSelectedVenueIds] = useState(null);
  const [dateFilter, setDateFilter] = useState(() => ({
    preset: DATE_RANGE_PRESET.LAST_7_DAYS,
    range: presetRange(DATE_RANGE_PRESET.LAST_7_DAYS),
  }));
  const [lifecycleStatus, setLifecycleStatus] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const venueIds = useMemo(
    () => selectedVenueIds ?? skuVenues.map(({ venueId }) => venueId),
    [selectedVenueIds, skuVenues]
  );
  const filter = useMemo(
    () => toOrdersReportFilter({ venueIds, range: dateFilter.range, lifecycleStatus }),
    [venueIds, dateFilter, lifecycleStatus]
  );
  const summaryScope = useMemo(
    () => toOrdersReportScope({ venueIds, range: dateFilter.range }),
    [venueIds, dateFilter]
  );

  const tracking = useMemo(
    () => makeReportsTracker({ page: REPORTS_PAGE.ORDERS, venueIds, isMobile }),
    [venueIds, isMobile]
  );
  useEffect(() => {
    tracking.pageViewed();
  }, []);

  const summaryQuery = useOrdersReportSummaryQuery(summaryScope);
  const {
    data: listing,
    isLoading: isListingLoading,
    isError: isListingError,
    refetch: refetchListing,
    page,
    changePage,
    nextPageCursor,
  } = useOrdersReportQuery(filter);
  const isListingFiltered = lifecycleStatus !== null || venueIds.length !== skuVenues.length;
  const { exportCsv, isExporting } = useOrdersReportCsvExport(filter, {
    onDownloaded: rowsExported =>
      tracking.csvDownloaded({
        rowsExported,
        isFiltered: isListingFiltered,
        filters: { status: (lifecycleStatus || 'all').toLowerCase(), 'venues selected': venueIds.length },
      }),
  });

  const summary = summaryQuery.isError ? undefined : summaryQuery.data;
  const summaryLoading = summaryQuery.isInitialLoading;

  const renderTrend = (currentValue, lastValue, positiveIsGood) => {
    if (!summary) return null;
    if (!lastValue) {
      return (
        <SummaryTileTrend>
          <SummaryTileTrendDelta>{EMPTY_VALUE}</SummaryTileTrendDelta>
        </SummaryTileTrend>
      );
    }
    const percent = ((currentValue - lastValue) / lastValue) * 100;
    const isGood = percent === 0 ? null : percent > 0 === positiveIsGood;
    return (
      <SummaryTileTrend>
        <SummaryTileTrendDelta isGood={isGood}>
          {percent !== 0 && <span aria-hidden>{percent > 0 ? '↗ ' : '↘ '}</span>}
          {intl.formatNumber(Math.abs(percent) / 100, { style: 'percent', maximumFractionDigits: 1 })}
        </SummaryTileTrendDelta>{' '}
        <Message message={messages.comparedToLastPeriod} />
      </SummaryTileTrend>
    );
  };

  const renderTileValue = content => {
    if (summaryLoading) return <LoadingDots />;
    if (!summary || content === null) return EMPTY_VALUE;
    return content;
  };

  const currencies = summary
    ? [
        ...new Set([...summary.current.earnings, ...summary.lastPeriod.earnings].map(entry => entry.currencyCode)),
      ].sort()
    : [];

  const timezoneByVenueId = Object.fromEntries(skuVenues.map(({ venueId, timezone }) => [venueId, timezone]));
  const orders = (listing?.orders || []).map(order => ({
    ...order,
    venueTimezone: timezoneByVenueId[order.core.venueId],
  }));
  const totalCount = listing?.totalCount || 0;
  const canDownloadCsv = totalCount > 0 && totalCount <= ORDERS_REPORT_CSV_MAX_ROWS;
  const csvButtonLabel = isExporting ? messages.downloadCsvGenerating : messages.downloadCsv;
  const selectedOrder = orders.find(order => order.core.orderId === selectedOrderId);

  const handleRowClick = order => {
    tracking.rowOpened({ entryPoint: 'row', 'order id': order.core.orderId });
    setSelectedOrderId(order.core.orderId);
    openFlyout(ORDER_FLYOUT_ID);
  };

  const handleStatusChange = key => {
    if ((lifecycleStatus ?? ALL_STATUSES) === key) return;
    if (key === ALL_STATUSES) tracking.filterRemoved(FILTER_TYPE.STATUS);
    else tracking.filterApplied(FILTER_TYPE.STATUS, { status: key.toLowerCase() });
    setLifecycleStatus(key === ALL_STATUSES ? null : key);
  };

  const statusOptions = STATUS_OPTIONS.map(({ key, label }) => ({ key, label: intl.formatMessage(label) }));

  return (
    <SkuReportsTrackingContext.Provider value={tracking}>
      <Message message={messages.pageTitle} as={Title} />

      <div className={block()}>
        <SkuOrderReportsFilters
          venues={skuVenues}
          selectedVenueIds={venueIds}
          onVenuesChange={setSelectedVenueIds}
          range={dateFilter.range}
          onRangeChange={range => setDateFilter({ preset: findMatchingPreset(range), range })}
          preset={dateFilter.preset}
          onPresetSelect={preset => setDateFilter({ preset, range: presetRange(preset) })}
        />

        <div className={element('tiles')}>
          <SummaryTile
            label={<Message message={messages.earningsTile} />}
            value={renderTileValue(
              currencies.length
                ? currencies.map(currencyCode => (
                    <React.Fragment key={currencyCode}>
                      <SummaryTileValueLine>
                        <Message
                          format="currency"
                          currency={currencyCode}
                          value={findEarnings(summary.current, currencyCode)}
                        />
                      </SummaryTileValueLine>
                      {renderTrend(
                        findEarnings(summary.current, currencyCode),
                        findEarnings(summary.lastPeriod, currencyCode),
                        true
                      )}
                    </React.Fragment>
                  ))
                : null
            )}
          />
          <SummaryTile
            label={<Message message={messages.ordersTile} />}
            value={renderTileValue(summary ? intl.formatNumber(summary.current.ordersCount) : null)}
            trend={summary && renderTrend(summary.current.ordersCount, summary.lastPeriod.ordersCount, true)}
          />
          <SummaryTile
            alert={Boolean(summary?.current.cancelledOrdersCount)}
            label={<Message message={messages.cancelledOrdersTile} />}
            value={renderTileValue(summary ? intl.formatNumber(summary.current.cancelledOrdersCount) : null)}
            trend={
              summary &&
              renderTrend(summary.current.cancelledOrdersCount, summary.lastPeriod.cancelledOrdersCount, false)
            }
          />
        </div>

        <div className={element('listing-header')}>
          <span className={element('listing-count')}>
            {listing && intl.formatMessage(messages.ordersCount, { count: totalCount })}
          </span>
          {canDownloadCsv && (
            <Button
              styled="inline-link"
              className={element('csv-button')}
              disabled={isExporting}
              onClick={exportCsv}
              aria-label={intl.formatMessage(csvButtonLabel)}>
              {isExporting ? <Spinner /> : <SvgDownload className={element('csv-icon')} />}
              {!isMobile && <Message message={csvButtonLabel} />}
            </Button>
          )}
        </div>

        <div className={element('listing-controls')}>
          <PillGroup options={statusOptions} value={lifecycleStatus ?? ALL_STATUSES} onChange={handleStatusChange} />
        </div>

        <SkuOrderReportsTable
          orders={orders}
          totalCount={totalCount}
          showLocationColumn={showLocationColumn}
          isMobile={isMobile}
          isLoading={isListingLoading}
          isError={isListingError}
          onRetry={() => refetchListing()}
          page={page}
          nextPageCursor={nextPageCursor}
          onChangePage={changePage}
          onRowClick={handleRowClick}
        />
      </div>

      <SkuOrderFlyout
        flyoutId={ORDER_FLYOUT_ID}
        order={selectedOrder}
        showOrderInfoRows
        venueTimezone={selectedOrder?.venueTimezone}
      />
    </SkuReportsTrackingContext.Provider>
  );
};

const SkuOrderReportsPage = () => (
  <FlyoutProvider>
    <SkuOrderReportsPageContent />
  </FlyoutProvider>
);

export default SkuOrderReportsPage;
